/**
 * Windowed device grid list (CAP-FE-2 / FE-4) — sync scroll, no extra API.
 * Pattern from purged immersive fleet-rail list (ZD-DEAD); fixed row estimate for row-thumb cards.
 */

/** Virtualize when visible rows >= this (FE-4). */
export const DEVICES_GRID_VIRTUAL_MIN = 50;

/** Estimated px per device row-thumb card (fixed-height windowing). */
export const DEVICES_GRID_VIRTUAL_ROW_PX = 118;

/** Estimated px per full device-card on #/group (taller than row-thumb). */
export const GROUP_PAGE_GRID_VIRTUAL_ROW_PX = 168;

/** Estimated px per overview group-card article (actions + KPI row). */
export const OVERVIEW_GROUP_CARD_VIRTUAL_ROW_PX = 320;

/**
 * @param {HTMLElement} container
 * @param {string[]} htmlRows
 * @param {object} [opts]
 * @param {number} [opts.rowPx]
 * @param {number} [opts.minRows]
 * @param {() => void} [opts.onSliceRendered]
 * @returns {() => void} dispose
 */
export function mountDeviceGridVirtualList(container, htmlRows, opts = {}) {
  const rowPx =
    typeof opts.rowPx === "number" && opts.rowPx > 0
      ? opts.rowPx
      : DEVICES_GRID_VIRTUAL_ROW_PX;
  const minRows =
    typeof opts.minRows === "number" && opts.minRows > 0
      ? opts.minRows
      : DEVICES_GRID_VIRTUAL_MIN;
  const prevOff = /** @type {any} */ (container)._crocGridVirtualOff;
  if (typeof prevOff === "function") {
    try {
      prevOff();
    } catch (_) {}
    /** @type {any} */ (container)._crocGridVirtualOff = null;
  }

  const savedScroll = /** @type {any} */ (container)._crocGridScrollTop;

  container.classList.remove("device-grid--virtual");
  container.style.maxHeight = "";
  container.style.overflowY = "";
  container.style.position = "";
  container.innerHTML = "";

  const rows = Array.isArray(htmlRows) ? htmlRows : [];
  if (!rows.length) {
    return () => {};
  }

  if (rows.length < minRows) {
    container.innerHTML = rows.join("");
    return () => {};
  }

  container.classList.add("device-grid--virtual");
  container.style.maxHeight = "min(72vh, 720px)";
  container.style.overflowY = "auto";
  container.style.position = "relative";

  const spacer = document.createElement("div");
  spacer.className = "device-grid__virtual-spacer";
  spacer.style.height = `${rows.length * rowPx}px`;
  spacer.setAttribute("aria-hidden", "true");

  const win = document.createElement("div");
  win.className = "device-grid__virtual-window device-grid";
  win.style.position = "absolute";
  win.style.left = "0";
  win.style.right = "0";
  win.style.top = "0";

  container.appendChild(spacer);
  container.appendChild(win);

  let raf = 0;
  const paint = () => {
    raf = 0;
    const top = container.scrollTop;
    const viewH = container.clientHeight || 480;
    const start = Math.max(0, Math.floor(top / rowPx) - 2);
    const visible = Math.ceil(viewH / rowPx) + 6;
    const end = Math.min(rows.length, start + visible);
    win.style.transform = `translateY(${start * rowPx}px)`;
    win.innerHTML = rows.slice(start, end).join("");
    if (typeof opts.onSliceRendered === "function") {
      try {
        opts.onSliceRendered();
      } catch (_) {}
    }
  };

  const onScroll = () => {
    if (raf) return;
    raf = window.requestAnimationFrame(paint);
  };

  container.addEventListener("scroll", onScroll, { passive: true });
  paint();

  if (typeof savedScroll === "number" && savedScroll > 0) {
    try {
      container.scrollTop = savedScroll;
      paint();
    } catch (_) {}
  }

  const dispose = () => {
    container.removeEventListener("scroll", onScroll);
    if (raf) {
      try {
        window.cancelAnimationFrame(raf);
      } catch (_) {}
      raf = 0;
    }
    /** @type {any} */ (container)._crocGridScrollTop = container.scrollTop;
    container.classList.remove("device-grid--virtual");
    container.style.maxHeight = "";
    container.style.overflowY = "";
    container.style.position = "";
    container.innerHTML = "";
    /** @type {any} */ (container)._crocGridVirtualOff = null;
  };

  /** @type {any} */ (container)._crocGridVirtualOff = dispose;
  return dispose;
}
